// @ts-strict-ignore
import type { ReactNode } from "react";
import { useState } from "react";

import { useEmersonCatalogParams } from "@emerson/hooks/useEmersonCatalogParams/useEmersonCatalogParams";
import type {
  Filter,
  CatalogSearchType,
  SearchFilterCategories,
  ProductSortField,
  ProductSortDirection,
  IngredientFilter,
  TaxonomyFilter,
} from "@shared/components/CatalogSearch/CatalogSearch";
import { CatalogSearchContext } from "@shared/components/CatalogSearch/CatalogSearchContext/CatalogSearchContext";
import { useSharedCatalogSearchProvider } from "@shared/hooks/useSharedCatalogSearchProvider/useSharedCatalogSearchProvider";
import type { EqpLevelType, Typeahead as TypeaheadType } from "@shared/types/graphqlGenerated";

interface Props {
  children: ReactNode;
}

const CatalogSearchProvider = ({ children }: Props) => {
  const [catalogParams, setCatalogParams] = useEmersonCatalogParams({ preserveScroll: false });

  const initFilters = {
    allergens: undefined,
    brands: undefined,
    certifications: undefined,
    conditions: undefined,
    excludedIngredients: undefined,
    forms: undefined,
    ingredients: undefined,
    isOnlyShowFavorites: undefined,
    isOnlyShowInStock: undefined,
    isNew: undefined,
    onlyIngredient: undefined,
    populations: undefined,
    supplementTypes: undefined,
    relevanceFilters: undefined,
    categories: undefined,
    eqpLevels: undefined,
    taxonomies: undefined,
    more: undefined,
  };

  const {
    activeBrands,
    activeIngredients,
    activeRelevanceFilters,
    activeSupplementTypes,
    activeConditions,
    activeTaxonomies,
    searchQuery,
    setActiveBrands,
    setActiveIngredients,
    setActiveSupplementTypes,
    setActiveConditions,
    setTypeaheadAsFilter,
    setClickedRecentSearchTypeaheadToParam,
  } = useSharedCatalogSearchProvider(catalogParams, setCatalogParams);

  const formFilter: Filter[] = catalogParams.forms || [];

  const allergenFilter: Filter[] = catalogParams.allergens || [];

  const moreFilters: Filter[] = catalogParams.more || [];

  const activeFilters: Filter[] = [...formFilter, ...allergenFilter, ...moreFilters];

  const activeEqpLevels: EqpLevelType[] = catalogParams.eqpLevels ?? [];

  const getActiveFilterByCategory = (filters: Filter[], category: SearchFilterCategories) =>
    filters?.filter(filter => filter.category === category);

  const setActiveFilters = (filters: Filter[]) => {
    const newFilters = {
      allergens: getActiveFilterByCategory(filters, "Allergens"),
      forms: getActiveFilterByCategory(filters, "Form"),
      more: getActiveFilterByCategory(filters, "More"),
    };
    setCatalogParams({ ...catalogParams, ...newFilters });
  };

  const excludedIngredients: IngredientFilter[] = catalogParams.excludedIngredients || [];

  const setExcludedIngredients = (excludedIngredientFilters: IngredientFilter[]) => {
    setCatalogParams({
      ...catalogParams,
      excludedIngredients: excludedIngredientFilters,
    });
  };

  const setActiveTaxonomies = (taxonomyFilters: TaxonomyFilter[]) => {
    setCatalogParams({
      ...catalogParams,
      taxonomies: taxonomyFilters,
    });
  };

  const setActiveEqpLevels = (eqpLevels: EqpLevelType[]) => {
    setCatalogParams({
      ...catalogParams,
      eqpLevels,
    });
  };

  const typeaheadId: string = catalogParams.typeaheadSelectionId ?? "";

  const setSearchQuery = (
    queryText: string,
    queryTypeaheadId?: string,
    clearAllPriorSearchParam?: boolean
  ) => {
    if (!queryText) {
      const {
        query: unusedQuery,
        typeaheadSelectionId: unusedTypeaheadSelectionId,
        ...catalogParamsWithoutQuery
      } = catalogParams;
      setCatalogParams(clearAllPriorSearchParam ? {} : catalogParamsWithoutQuery);
    } else if (!queryTypeaheadId) {
      setCatalogParams({
        ...(!clearAllPriorSearchParam && { ...initFilters }),
        query: queryText,
      });
    } else {
      setCatalogParams({
        ...(!clearAllPriorSearchParam && { ...catalogParams }),
        query: queryText,
        typeaheadSelectionId: queryTypeaheadId,
      });
    }
  };

  const handleSetTypeaheadAsFilter = (
    typeaheadSearch: Pick<TypeaheadType, "id" | "name" | "entityType">,
    clearAllPriorSearchParam?: boolean
  ) => {
    setTypeaheadAsFilter(typeaheadSearch, initFilters, clearAllPriorSearchParam);
  };

  const sortField: ProductSortField = catalogParams.sortField;
  const sortDirection: ProductSortDirection = catalogParams.sortDirection;

  const setSort = (field: ProductSortField, direction: ProductSortDirection) => {
    setCatalogParams({
      ...catalogParams,
      sortField: field,
      sortDirection: direction,
    });
  };

  const [activeSearchType, setActiveSearchType] = useState<CatalogSearchType>("filters");

  const isOnlyShowInStock = catalogParams.isOnlyShowInStock;
  const setIsOnlyShowInStock = (value: boolean) =>
    setCatalogParams({ ...catalogParams, isOnlyShowInStock: value });

  const isOnlyShowEmersonNew = catalogParams.isNew;
  const setIsOnlyShowEmersonNew = (value: boolean) =>
    setCatalogParams({ ...catalogParams, isNew: value });

  const removeActiveFilter = (filterToRemove: Filter) => {
    setActiveFilters(
      activeFilters.filter(
        activeFilter =>
          activeFilter.name !== filterToRemove.name ||
          activeFilter.category !== filterToRemove.category
      )
    );
  };

  const onlyIngredient = catalogParams.onlyIngredient || false;
  const setOnlyIngredient = (toggle: boolean) => {
    if (excludedIngredients.length > 0) {
      setCatalogParams({
        ...catalogParams,
        excludedIngredients: [],
        onlyIngredient: toggle,
      });
    } else {
      setCatalogParams({ ...catalogParams, onlyIngredient: toggle });
    }
  };

  const activeTypeaheadQuery = () => {
    const activeFilterTags: any = [...activeBrands, ...activeTaxonomies, ...activeIngredients];

    const hasOnlyOneActiveFilter = activeFilterTags?.length === 1;
    if (hasOnlyOneActiveFilter) {
      return activeFilterTags?.[0]?.name;
    }

    return null;
  };

  return (
    <CatalogSearchContext
      value={{
        activeFilters,
        setActiveFilters,
        activeBrands,
        setActiveBrands,
        activeIngredients,
        setActiveIngredients,
        excludedIngredients,
        setExcludedIngredients,
        activeSupplementTypes,
        setActiveSupplementTypes,
        activeConditions,
        setActiveConditions,
        activeTaxonomies,
        setActiveTaxonomies,
        activeEqpLevels,
        setActiveEqpLevels,
        searchQuery,
        setSearchQuery,
        removeActiveFilter,
        activeSearchType,
        setActiveSearchType,
        sortDirection,
        sortField,
        setSort,
        onlyIngredient,
        setOnlyIngredient,
        isOnlyShowInStock,
        setIsOnlyShowInStock,
        isOnlyShowEmersonNew,
        setIsOnlyShowEmersonNew,
        typeaheadSelectionId: typeaheadId,
        typeaheadSelectionName: activeTypeaheadQuery(),
        setTypeaheadAsFilter: handleSetTypeaheadAsFilter,
        activeRelevanceFilters,
        setClickedRecentSearchTypeaheadToParam,
      }}
    >
      {children}
    </CatalogSearchContext>
  );
};

export { CatalogSearchProvider };
